import {
  DesktopOutlined,
  EditOutlined,
  EllipsisOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  UploadOutlined,
  UserAddOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Drawer,
  Dropdown,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Upload,
  message,
  Input
} from 'antd'
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { PageHeader } from '~/components/PageHeader'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { clipboard, compareNumber, compareText } from '~/utils/helpers'
import { useAppstreamsQuery } from '../../CourseDetails/CourseServers/query'
import { ParticipantForm } from '../../CourseDetails/CourseParticipants/StudentForm'
import { useDeleteparticipantMutation } from '../../CourseDetails/CourseParticipants/mutation'
import { courseParticipantQueryKey } from '../../CourseDetails/CourseParticipants/query'
import {
  useImportStudentMutation,
  useSendAllLoginDetailMutation,
  useSendStudentLoginDetailMutation,
  useStudentEnabledMutation,
  useStudentPreAccessMutation
} from './mutation'
import { useCourseParticipantsQuery, useCourseQuery, useCourseServersQuery } from './query'

const { confirm } = Modal

const WorkstationDetails = () => {
  const { id } = useParams()
  const { data, isLoading } = useCourseParticipantsQuery(id)
  const dataSource = useTableDataSourceKey(data)
  const [filterInput, setFilterInput] = useState('')
  const { mutate: importStudent, isLoading: uploading } = useImportStudentMutation(id)
  const { mutate: sendLoginDetails, isLoading: isSendingLoginDetails } = useSendAllLoginDetailMutation(id)
  const { data: servers } = useCourseServersQuery(id)
  const { data: appstreams } = useAppstreamsQuery(id)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const { data: course } = useCourseQuery(id)
  const { mutate: sendStudentLogin } = useSendStudentLoginDetailMutation(id)
  const { mutate: studentEnabled } = useStudentEnabledMutation(id)
  const { mutate: studentPreAccess } = useStudentPreAccessMutation(id)
  const queryClient = useQueryClient()

  const serverFilters = useMemo(() => {
    if (servers.length === 0) {
      return []
    }

    return servers.map((i) => {
      return {
        text: i.name,
        value: i.name
      }
    })
  }, [servers])

  const appstreamFilters = useMemo(() => {
    if (appstreams == null) {
      return []
    }

    return appstreams.map((i) => {
      return {
        text: i.stack_name,
        value: i.stack_name
      }
    })
  }, [appstreams])

  const confirmSendLogin = (item) => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to send the login details to ${item.first_name}?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        sendStudentLogin({ studentId: item.id })
      }
    })
  }

  const confirmEnabled = (item) => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to ${item.enabled ? 'disable' : 'enable'} the student?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        studentEnabled(item.id)
      }
    })
  }

  const confirmPreAccess = (item) => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to ${item.pre_access ? 'disable' : 'enable'
        } pre-access to the student?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        studentPreAccess(item.id)
      }
    })
  }

  const { mutate } = useDeleteparticipantMutation(id)
  const confirmDelete = (item) => {
    confirm({
      title: 'Confirm',
      icon: <QuestionCircleOutlined />,
      content: `Do you want to delete the student '${[item.first_name, item.last_name].join(
        ' '
      )}'?`,
      okText: 'YES',
      okType: 'danger',
      cancelText: 'NO',
      onOk: () => {
        mutate(item.id)
      }
    })
  }

  const filterData = useMemo(() => {
    const filter = filterInput.toLowerCase()
    if (!dataSource) {
      return []
    }

    return dataSource.filter(
      (student) =>
        `${student.first_name} ${student.last_name}`.toLowerCase().includes(filter) ||
        student.email.toLowerCase().includes(filter)
    )
  }, [dataSource, filterInput])

  if (!course) {
    return null
  }

  return (
    <>
      <PageHeader
        className="radup-page-header"
        title={'Workstations > ' + course?.name}
        extra={[
          <Col key={1}>
            <Row style={{ marginBottom: '10px' }}>
              <Input.Search
                placeholder="Search"
                allowClear
                enterButton={<SearchOutlined />}
                onSearch={setFilterInput}
              />
            </Row>
            <Row>
              <Space>
                <Upload customRequest={importStudent} showUploadList={false}>
                  <Button type="default" icon={<UploadOutlined />} loading={uploading}>
                    Import participants
                  </Button>
                </Upload>
                <Popconfirm
                  title="Do you want to send the login details to all participants?"
                  onConfirm={sendLoginDetails}
                  okText="YES"
                >
                  <Button type="primary" loading={isSendingLoginDetails} icon={<MailOutlined />}>
                    Send all login details
                  </Button>
                </Popconfirm>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => {
                    setIsDrawerOpen(true)
                  }}
                >
                  New Participant
                </Button>
              </Space>
            </Row>
          </Col>
        ]}
        onBack={() => window.history.back()}
      />

      <Table
        size="small"
        tableLayout="auto"
        dataSource={filterData}
        loading={isLoading}
        footer={() => (
          <Row justify="start">
            <span>Total: {filterData.length} participants</span>
          </Row>
        )}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a, b) => compareNumber(a.id, b.id)
          },
          {
            title: 'Alias',
            render: (v, student) => student?.alias,
            sorter: (a, b) => compareText(a.alias, b.alias)
          },
          {
            title: 'Name',
            render: (v, student) => `${student?.first_name} ${student?.last_name}`,
            sorter: (a, b) =>
              compareText(`${a.first_name} ${a.last_name}`, `${b.first_name} ${b.last_name}`)
          },
          {
            title: 'Email',
            render: (student) => student?.email,
            sorter: (a, b) => compareText(a.email, b.email)
          },
          {
            title: 'Username',
            render: (student) => student?.username
          },
          {
            title: 'Password',
            render: (student) => student?.password
          },
          {
            title: 'Server',
            filters: serverFilters,
            onFilter: (value, record) => (record.server?.name ?? '').includes(value.toString()),
            render: (item) => item.server?.name
          },
          {
            title: 'Appstream',
            render: (student) => student.appstream?.stack_name,
            onFilter: (value, record) => (record.server?.name ?? '').includes(value.toString()),
            filters: appstreamFilters
          },
          {
            title: 'Pre-access',
            render: (v, item) => (item?.pre_access ? 'Enable' : 'Disable')
          },
          {
            title: 'Actions',
            render: (item) => {
              return (
                <Space size="middle">
                  <Button
                    key={1}
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setIsDrawerOpen(true)
                      setEditing(item)
                    }}
                    disabled={item.enabled === false}
                  />
                  <a href={item.workstation_link} target="_blank" rel="noreferrer" key='workstation_link'>
                    <Button
                      type="primary"
                      icon={<DesktopOutlined />}
                      title='Workstation'
                    />
                  </a>
                  <Dropdown
                    key={2}
                    className="dropdown-action"
                    placement="bottom"
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          onClick: () => { confirmSendLogin(item) },
                          disabled: item.enabled === false,
                          label: 'Send login info',
                          key: 'Send login info'
                        },
                        {
                          key: 'Copy Link',
                          label: 'Copy Link',
                          disabled: item.enabled === false,
                          onClick: () => {
                            clipboard(item.workstation_link)
                            message.success('Copied')
                          }
                        },
                        {
                          key: 'enable',
                          label: item.enabled === true ? 'Disable' : 'Enable',
                          disabled: item.enabled === false,
                          onClick: () => { confirmEnabled(item) }
                        },
                        {
                          key: 'pre_access',
                          label: `${item.pre_access === true ? 'Disable' : 'Enable'} Pre-access`,
                          disabled: item.enabled === false,
                          onClick: () => { confirmPreAccess(item) }
                        },
                        {
                          key: 'delete',
                          label: 'Delete',
                          onClick: () => { confirmDelete(item) }
                        }
                      ]
                    }}
                  >
                    <Button type="primary" icon={<EllipsisOutlined />} />
                  </Dropdown>
                </Space>
              )
            }
          }
        ]}
      />

      <Drawer
        title="Create/Update Workstation"
        placement="right"
        closable={false}
        size={720}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
          setEditing(null)
        }}
        destroyOnHidden
      >
        <ParticipantForm
          courseId={Number(id)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: courseParticipantQueryKey(id) })
            setIsDrawerOpen(false)
            setEditing(null)
          }}
          item={editing}
        />
      </Drawer>
    </>
  )
}

export default WorkstationDetails
